import { useMemo, useState } from "react";
import type {
  EventId,
  PlanEvent,
  Task,
  TaskId,
} from "../../../../shared/types";
import {
  getToday,
  addDays,
  getDayName,
  getDayNumber,
  getMonthYear,
} from "../../../../shared/types/date-helpers";
import { EmptyState, DayAgenda } from "./AgendaComponents";

type AgendaLayout = "vertical" | "horizontal";

const STORAGE_KEY = "palnia_agenda_layout";

function getStoredLayout(): AgendaLayout {
  const stored = localStorage.getItem(STORAGE_KEY);
  if (stored === "horizontal") return "horizontal";
  return "vertical";
}

function getDateHeader(dateStr: string): string {
  const today = getToday();
  if (dateStr === today) return "Aujourd'hui";
  if (dateStr === addDays(today, 1)) return "Demain";
  const dayName =
    getDayName(dateStr).charAt(0).toUpperCase() + getDayName(dateStr).slice(1);
  const dayNum = getDayNumber(dateStr);
  const monthYear = getMonthYear(dateStr);
  return `${dayName} ${dayNum} ${monthYear}`;
}

interface AgendaViewProps {
  readonly events: PlanEvent[];
  readonly tasks: Task[];
  readonly onCycleStatus: (id: TaskId) => void;
  readonly onDeleteEvent: (id: EventId) => void;
  readonly onUpdateEvent: (
    id: EventId,
    updates: Partial<
      Pick<
        PlanEvent,
        | "title"
        | "date"
        | "startTime"
        | "endTime"
        | "notes"
        | "description"
        | "tags"
        | "recurrence"
        | "reminderMinutes"
        | "category"
      >
    >,
  ) => void;
  readonly onAddRecurrenceException?: (baseId: EventId, date: string) => void;
}

export function AgendaView({
  events,
  tasks,
  onCycleStatus,
  onDeleteEvent,
  onUpdateEvent,
  onAddRecurrenceException,
}: AgendaViewProps) {
  const [layout, setLayout] = useState<AgendaLayout>(getStoredLayout);
  const [daysToShow, setDaysToShow] = useState(14);

  const toggleLayout = () => {
    const next = layout === "vertical" ? "horizontal" : "vertical";
    setLayout(next);
    localStorage.setItem(STORAGE_KEY, next);
  };

  const loadMoreDays = () => {
    setDaysToShow((prev) => Math.min(prev + 14, 90));
  };

  const agendaData = useMemo(() => {
    const today = getToday();
    const dateMap = new Map<string, { events: PlanEvent[]; tasks: Task[] }>();

    for (let i = 0; i < daysToShow; i++) {
      const date = addDays(today, i);
      dateMap.set(date, { events: [], tasks: [] });
    }

    for (const event of events) {
      const bucket = dateMap.get(event.date);
      if (bucket) {
        bucket.events.push(event);
      }
    }

    for (const task of tasks) {
      if (task.dueDate && !task.eventId && !task.archived) {
        const bucket = dateMap.get(task.dueDate);
        if (bucket) {
          bucket.tasks.push(task);
        }
      }
    }

    const result: Array<{ date: string; events: PlanEvent[]; tasks: Task[] }> =
      [];
    for (let i = 0; i < daysToShow; i++) {
      const date = addDays(today, i);
      const bucket = dateMap.get(date)!;
      if (bucket.events.length > 0 || bucket.tasks.length > 0) {
        result.push({
          date,
          events: bucket.events.sort((a, b) =>
            a.startTime.localeCompare(b.startTime),
          ),
          tasks: bucket.tasks,
        });
      }
    }

    return result;
  }, [events, tasks, daysToShow]);

  if (agendaData.length === 0) {
    return <EmptyState />;
  }

  return (
    <div className={layout === "horizontal"
      ? "w-full max-w-full md:max-w-[80%] mx-auto py-4 pb-20 md:pb-4"
      : "w-full max-w-full md:max-w-[40%] mx-auto py-4 pb-20 md:pb-4"
    }>
      {/* Header avec contrôles */}
      <div className="flex items-center justify-between px-2 mb-3">
        <span className="text-[10px] text-ink-faint uppercase tracking-wide">
          {agendaData.length} jour{agendaData.length > 1 ? "s" : ""}
        </span>
        <button
          type="button"
          onClick={toggleLayout}
          className="hidden sm:flex items-center gap-1.5 px-2 py-1 text-[10px] font-medium
            rounded-md bg-surface border border-border text-ink-muted
            hover:text-ink hover:border-accent/60 transition-colors"
        >
          {layout === "vertical" ? (
            <>
              <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
              </svg>
              <span>Vertical</span>
            </>
          ) : (
            <>
              <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 4v16M15 4v16" />
              </svg>
              <span>Horizontal</span>
            </>
          )}
        </button>
      </div>

      {/* Contenu */}
      {layout === "horizontal" ? (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {agendaData.map(({ date, events: dayEvents, tasks: dayTasks }) => (
              <DayAgenda
                key={date}
                date={date}
                dateHeader={getDateHeader(date)}
                events={dayEvents}
                tasks={dayTasks}
                onCycleStatus={onCycleStatus}
                onDeleteEvent={onDeleteEvent}
                onUpdateEvent={onUpdateEvent}
                onAddRecurrenceException={onAddRecurrenceException}
              />
            ))}
          </div>
          {/* Bouton Voir plus */}
          <div className="flex justify-center my-6">
            {daysToShow < 90 ? (
              <button
                type="button"
                onClick={loadMoreDays}
                className="px-4 py-2.5 text-xs font-medium text-accent hover:text-white hover:bg-accent/80 bg-accent/10 rounded-lg transition-colors flex items-center gap-1.5"
              >
                Voir plus ({daysToShow}/{90} jours)
                <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>
            ) : (
              <span className="text-[10px] text-ink-faint">
                {agendaData.length} jour{agendaData.length > 1 ? "s" : ""} avec contenu sur {daysToShow}
              </span>
            )}
          </div>
        </>
      ) : (
        <div className="space-y-4">
          {agendaData.map(({ date, events: dayEvents, tasks: dayTasks }) => (
            <DayAgenda
              key={date}
              date={date}
              dateHeader={getDateHeader(date)}
              events={dayEvents}
              tasks={dayTasks}
              onCycleStatus={onCycleStatus}
              onDeleteEvent={onDeleteEvent}
              onUpdateEvent={onUpdateEvent}
            />
          ))}
          {/* Bouton Voir plus */}
          <div className="flex justify-center my-6">
            {daysToShow < 90 ? (
              <button
                type="button"
                onClick={loadMoreDays}
                className="px-4 py-2.5 text-xs font-medium text-accent hover:text-white hover:bg-accent/80 bg-accent/10 rounded-lg transition-colors flex items-center gap-1.5"
              >
                Voir plus ({daysToShow}/{90} jours)
                <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>
            ) : (
              <span className="text-[10px] text-ink-faint">
                {agendaData.length} jour{agendaData.length > 1 ? "s" : ""} avec contenu sur {daysToShow}
              </span>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
